import { BufferAttribute, BufferGeometry, MeshBasicMaterial, Vector4 } from "three";
import type { VehicleDims } from "../dims.js";

/**
 * Light panels of a vehicle: one small quad per lamp a few millimetres outside the body at the
 * lamp positions the ingest measured (head, tail, reverse, indicators), all in one geometry with a
 * lamp-type attribute. One material per car carries the intensities (off lamps are discarded), so
 * the panels cost one draw call and glow (values above 1 feed the bloom).
 */

export const LAMP = { head: 0, tail: 1, reverse: 2, indL: 3, indR: 4 } as const;

const COLORS: Record<number, [number, number, number]> = {
  0: [1, 0.95, 0.82],
  1: [1, 0.015, 0.01],
  2: [1, 1, 1],
  3: [1, 0.5, 0.04],
  4: [1, 0.5, 0.04],
};

export function lampGeometry(d: VehicleDims): BufferGeometry {
  const big = d.length > 8;
  const pos: number[] = [];
  const col: number[] = [];
  const kind: number[] = [];
  const index: number[] = [];
  const quad = (x: number, y: number, z: number, w: number, h: number, dir: 1 | -1, k: number) => {
    const b = pos.length / 3;
    const zz = z + dir * 0.018;
    for (const [dx, dy] of [
      [-w / 2, -h / 2],
      [w / 2, -h / 2],
      [w / 2, h / 2],
      [-w / 2, h / 2],
    ] as const) {
      pos.push(x + dx, y + dy, zz);
      col.push(...COLORS[k]!);
      kind.push(k);
    }
    // Facing +Z (dir 1) or -Z.
    if (dir > 0) index.push(b, b + 1, b + 2, b, b + 2, b + 3);
    else index.push(b, b + 2, b + 1, b, b + 3, b + 2);
  };
  const s = big ? 1.5 : d.length < 2.5 ? 0.6 : 1;
  for (const p of d.lamps.head) {
    const [x = 0, y = 0, z = 0] = p;
    quad(x, y, z, 0.26 * s, 0.1 * s, 1, LAMP.head);
    quad(
      x + Math.sign(x) * 0.17 * s,
      y,
      z - 0.03,
      0.07 * s,
      0.06 * s,
      1,
      x > 0 ? LAMP.indL : LAMP.indR,
    );
  }
  for (const p of d.lamps.tail) {
    const [x = 0, y = 0, z = 0] = p;
    quad(x, y, z, 0.24 * s, 0.11 * s, -1, LAMP.tail);
    quad(x - Math.sign(x) * 0.16 * s, y, z + 0.01, 0.08 * s, 0.07 * s, -1, LAMP.reverse);
    quad(
      x + Math.sign(x) * 0.15 * s,
      y,
      z + 0.02,
      0.06 * s,
      0.06 * s,
      -1,
      x > 0 ? LAMP.indL : LAMP.indR,
    );
  }
  const g = new BufferGeometry();
  g.setAttribute("position", new BufferAttribute(new Float32Array(pos), 3));
  g.setAttribute("color", new BufferAttribute(new Float32Array(col), 3));
  g.setAttribute("aLamp", new BufferAttribute(new Float32Array(kind), 1));
  g.setIndex(index);
  g.computeBoundingSphere();
  return g;
}

/** Per-car lamp state: head, tail (brake raises it), reverse, indicators. */
export interface LampState {
  head: number;
  tail: number;
  reverse: number;
  indL: number;
  indR: number;
}

export function lampMaterial(): MeshBasicMaterial & { userData: { lamps: Vector4; ind: Vector4 } } {
  const lamps = new Vector4(0, 0, 0, 0);
  const ind = new Vector4(0, 0, 0, 0);
  const m = new MeshBasicMaterial({ vertexColors: true, toneMapped: false });
  m.polygonOffset = true;
  m.polygonOffsetFactor = -2;
  m.userData = { lamps, ind };
  m.customProgramCacheKey = () => "uz-lamps";
  m.onBeforeCompile = (shader) => {
    shader.uniforms["uLamps"] = { value: lamps };
    shader.uniforms["uInd"] = { value: ind };
    shader.vertexShader = shader.vertexShader
      .replace(
        "#include <common>",
        "#include <common>\nattribute float aLamp;\nvarying float vLamp;",
      )
      .replace("#include <begin_vertex>", "#include <begin_vertex>\nvLamp = aLamp;");
    shader.fragmentShader = shader.fragmentShader
      .replace(
        "#include <common>",
        "#include <common>\nuniform vec4 uLamps;\nuniform vec4 uInd;\nvarying float vLamp;",
      )
      .replace(
        "#include <color_fragment>",
        `#include <color_fragment>
        float k = vLamp < 0.5 ? uLamps.x : vLamp < 1.5 ? uLamps.y : vLamp < 2.5 ? uLamps.z : vLamp < 3.5 ? uInd.x : uInd.y;
        if (k < 0.01) discard;
        diffuseColor.rgb *= k;`,
      );
  };
  return m as MeshBasicMaterial & { userData: { lamps: Vector4; ind: Vector4 } };
}

export function setLamps(m: ReturnType<typeof lampMaterial>, s: LampState): void {
  m.userData.lamps.set(s.head, s.tail, s.reverse, 0);
  m.userData.ind.set(s.indL, s.indR, 0, 0);
}
